export const NULL_FILTER_VALUE = '__null__'

/**
 * Normalize a raw cell value into a stable filter value string.
 */
export const toFilterValue = (raw) => {
	if (raw === null || raw === undefined || raw === '') {
		return NULL_FILTER_VALUE
	}
	return String(raw)
}

/**
 * Derive unique { value, label, count } options from a row set.
 */
export const deriveFilterOptions = (rows, getValue, getLabel = getValue) => {
	const seen = new Map()
	;(rows || []).forEach((row) => {
		const value = toFilterValue(getValue(row))
		if (seen.has(value)) {
			seen.get(value).count += 1
			return
		}
		const labelRaw = getLabel(row)
		const label =
			value === NULL_FILTER_VALUE
				? labelRaw === null || labelRaw === undefined || labelRaw === ''
					? '(Blank)'
					: String(labelRaw)
				: labelRaw === null || labelRaw === undefined || labelRaw === ''
					? value
					: String(labelRaw)
		seen.set(value, { value, label, count: 1 })
	})
	return Array.from(seen.values()).sort((a, b) =>
		String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
	)
}

/**
 * Apply multi-column checkbox filters.
 * Missing key or empty array = no restriction for that column.
 * Non-empty array = include only those values.
 */
export const applyColumnFilters = (rows, filters, getValueByField) => {
	if (!filters || !Object.keys(filters).length) {
		return rows || []
	}

	return (rows || []).filter((row) =>
		Object.keys(filters).every((field) => {
			const selected = filters[field]
			if (selected === undefined || selected === null) {
				return true
			}
			if (!Array.isArray(selected) || selected.length === 0) {
				return true
			}
			const getValue = getValueByField[field]
			const value = toFilterValue(getValue ? getValue(row) : row[field])
			return selected.map(String).includes(value)
		})
	)
}

/**
 * Whether any column has at least one selected include value.
 */
export const hasActiveColumnFilters = (filters) => {
	if (!filters) {
		return false
	}
	return Object.keys(filters).some((field) => {
		const selected = filters[field]
		return Array.isArray(selected) && selected.length > 0
	})
}

/**
 * Whether a specific column has an active (non-empty) include filter.
 */
export const isColumnFilterActive = (filters, field) => {
	return !!(filters && Array.isArray(filters[field]) && filters[field].length > 0)
}

/**
 * Toggle one include value.
 * First check creates the field; unchecking the last value removes the field (no filter).
 */
export const toggleFilterValue = (filters, field, value) => {
	const next = { ...(filters || {}) }
	const stringValue = String(value)
	const current = Array.isArray(next[field]) ? next[field].map(String) : []
	const index = current.indexOf(stringValue)

	if (index >= 0) {
		current.splice(index, 1)
	} else {
		current.push(stringValue)
	}

	if (current.length === 0) {
		delete next[field]
	} else {
		next[field] = current
	}
	return next
}

/**
 * Replace the include-list for a column.
 * Empty selection removes the field (no filter / show all).
 */
export const setColumnFilterSelection = (filters, field, values) => {
	const next = { ...(filters || {}) }
	const selected = (Array.isArray(values) ? values : []).map(String)
	if (selected.length === 0) {
		delete next[field]
	} else {
		next[field] = selected
	}
	return next
}

/**
 * Merge selected values into a filter options list so orphaned selections
 * (e.g. after cascading from another column) stay visible and checked.
 * Orphaned options are pinned at the top. Labels prefer labelByValue, then
 * fall back to the value string (or "(Blank)" for the null sentinel).
 * Orphans use count 0 when not present in cascading options.
 *
 * @param {Array<{value: string, label: string, count?: number}>} options
 * @param {string[]} selectedValues
 * @param {Record<string, string>|Map<string, string>} [labelByValue]
 */
export const mergeSelectedIntoOptions = (options, selectedValues, labelByValue = {}) => {
	const base = Array.isArray(options) ? options : []
	const selected = (Array.isArray(selectedValues) ? selectedValues : []).map(String)
	if (selected.length === 0) {
		return base
	}

	const getLabel = (value) => {
		if (labelByValue instanceof Map) {
			if (labelByValue.has(value)) {
				return labelByValue.get(value)
			}
		} else if (labelByValue && Object.prototype.hasOwnProperty.call(labelByValue, value)) {
			return labelByValue[value]
		}
		return value === NULL_FILTER_VALUE ? '(Blank)' : value
	}

	const available = new Set(base.map((option) => String(option.value)))
	const orphans = []
	selected.forEach((value) => {
		if (available.has(value)) {
			return
		}
		orphans.push({ value, label: String(getLabel(value)), count: 0 })
		available.add(value)
	})

	if (orphans.length === 0) {
		return base
	}
	return [...orphans, ...base]
}
